/**
 * Utilitaires pour l'affichage des tarifs (périodes, libellés, prix).
 */

import type {
  PeriodeTarif,
  Tarif,
  LibelleMultilingue,
} from "../types/apidae.d";

const DEVISE_SYMBOLS: Record<string, string> = {
  EUR: "€",
  CHF: "CHF",
  XPF: "F",
  XOF: "CFA",
};

/**
 * Formate "Du dd/mm/yyyy au dd/mm/yyyy" pour une période tarifaire.
 */
export function formatPricingPeriodLabel(
  periode: PeriodeTarif,
  formatDate: (dateStr: string, options?: Intl.DateTimeFormatOptions) => string,
  labels?: { periodFrom?: string; periodTo?: string },
): string {
  const from = labels?.periodFrom ?? "Du";
  const to = labels?.periodTo ?? "au";
  const dateDebut = formatDate(periode.dateDebut, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  if (periode.dateFin && periode.dateFin !== periode.dateDebut) {
    const dateFin = formatDate(periode.dateFin, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    return `${from} ${dateDebut} ${to} ${dateFin}`;
  }
  return `${from} ${dateDebut}`;
}

function getDeviseSymbol(devise: string | undefined): string {
  if (!devise) return "€";
  return DEVISE_SYMBOLS[devise] ?? devise;
}

/**
 * Formate le prix d'un tarif : "De X€ à Y€", "X€" ou "X€ (Y€ en chambre)" si precisionTarif.
 * getLabel pour traduire precisionTarif (LibelleMultilingue).
 */
export function formatTarifPrice(
  tarif: Tarif,
  getLabel: (libelle: LibelleMultilingue | undefined) => string,
): string {
  const min = tarif.minimum;
  const max = tarif.maximum;
  const sym = getDeviseSymbol(tarif.devise);
  const fmt = (n: number) =>
    Number.isInteger(n)
      ? `${n} ${sym}`
      : `${n.toFixed(2).replace(".", ",")} ${sym}`;

  let priceText: string;
  if (min != null && max != null && min !== max) {
    priceText = `De ${fmt(min)} à ${fmt(max)}`;
  } else if (min != null) {
    priceText = fmt(min);
  } else if (max != null) {
    priceText = fmt(max);
  } else {
    priceText = "";
  }

  const precision = getLabel(tarif.precisionTarif);
  if (precision?.trim()) {
    return priceText ? `${priceText} (${precision})` : precision;
  }
  return priceText;
}
